import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Text,
  TouchableOpacity,
  View,
  FlatList,
  Alert,
  KeyboardAvoidingView,
  Platform,
  TextInput,
} from 'react-native';
import Feather from 'react-native-vector-icons/Feather';
import { styles } from './styles';
import { Colors } from '../../constants/Colors';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import Header from '../../components/Header/Header';
import { useNavigation } from '@react-navigation/native';
import { NavigationProps } from '../../navigation/Routes';
import AuthService from '../../services/AuthService/AuthService';
import { useUserStore } from '../../store/UserStore';
import { useAuthStore } from '../../store/AuthStore';
import TextField from '../../components/TextField/TextField';
import { useForm } from 'react-hook-form';
import { cnpj, cpf } from 'cpf-cnpj-validator';
import { getAnalytics, logEvent } from '@react-native-firebase/analytics';
import { AppRoutes } from '../../navigation/Routes.types';
import { showSuccess } from '../../utils/globalAlert';

const RequestCompany: React.FC = () => {
  const [document, setDocument] = useState<string>('');
  const documentRef = useRef<string>('');
  const [documentsList, setDocumentsList] = useState<Array<string>>([]);
  const [documentError, setDocumentError] = useState<string | undefined>(undefined);
  const { navigate } = useNavigation<NavigationProps>();
  const inputRef = useRef<TextInput>(null);
  const { user } = useUserStore();
  const { token } = useAuthStore();
  const getGroupRequests = AuthService.getGroupRequests;
  const postGroupRequests = AuthService.postGroupRequests;
  const { register, setValue, handleSubmit, getValues } = useForm();
  const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const analyticsInstance = getAnalytics();

  useEffect(() => {
    register('name');
    register('document');
  }, [register]);

  const onSubmit = async () => {
    try {
      setLoadingSubmit(true);
      const { name } = getValues();
      const groupName = user?.group?.name || name;
      await postGroupRequests(token, groupName, user?.user, documentsList);
      showSuccess('Sucesso', 'Solicitação enviada com sucesso.');
      setDocument('');
      setDocumentsList([]);
      (navigate as any)(AppRoutes.MAIN_TAB_SCREEN, { screen: 'Home' });
    } catch (error) {
      if (error.response?.data?.message) {
        Alert.alert('Erro', error.response.data.message);
      } else {
        Alert.alert('Erro', 'Erro ao enviar solicitação.');
      }
      console.log(error);
    } finally {
      setLoadingSubmit(false);
    }
  }

  const addNumberToList = useCallback(() => {
    const digits = documentRef.current.replace(/\D/g, '');

    if (!digits) {
      Alert.alert('Erro', 'Digite um documento válido.');
      setDocumentError('Informe apenas números');
      return;
    }

    if (digits.length !== 11 && digits.length !== 14) {
      Alert.alert('Erro', 'Documento deve ter 11 dígitos (CPF) ou 14 dígitos (CNPJ).');
      setDocumentError('Quantidade de dígitos inválida');
      return;
    }

    const isValid = digits.length === 11 ? cpf.isValid(digits) : cnpj.isValid(digits);
    if (!isValid) {
      Alert.alert('Erro', 'Documento inválido.');
      setDocumentError('Documento inválido');
      return;
    }

    let duplicate = false;
    setDocumentsList((prevList) => {
      if (prevList.includes(digits)) {
        Alert.alert('Erro', 'Documento já existe na lista.');
        duplicate = true;
        return prevList;
      }
      return [digits, ...prevList];
    });
    if (duplicate) {
      setDocumentError('Documento já adicionado');
      return;
    }
    setDocumentError(undefined);
    setDocument('');
    documentRef.current = '';
  }, []);

  const handleClose = () => {
    setDocument('');
    setDocumentsList([]);
    (navigate as any)(AppRoutes.MAIN_TAB_SCREEN, { screen: 'Home' });
  };

  const removeNumberFromList = useCallback((index: number) => {
    setDocumentsList((prevList) => prevList.filter((_, i) => i !== index));
  }, []);

  const handleEdit = useCallback((item: string, index: number) => {
    removeNumberFromList(index);
    setDocument(item);
  }, [removeNumberFromList]);

  const renderNumberItem = useCallback(({ item, index }: { item: string; index: number }) => (
    <View style={styles.numberItemContainer}>
      <Text style={styles.textNumber}>{item}</Text>
      <View style={{ flexDirection: 'row', gap: 15 }}>
        <TouchableOpacity onPress={() => handleEdit(item, index)}>
          <Feather name="edit" size={18} color="black" />
        </TouchableOpacity>
        <TouchableOpacity onPress={() => removeNumberFromList(index)}>
          <Feather name="trash" size={18} color="black" />
        </TouchableOpacity>
      </View>
    </View>
  ), [handleEdit, removeNumberFromList]);

  const handleTextChange = (text: string) => {
    const digits = text.replace(/\D/g, '');
    documentRef.current = digits;
    setDocument(digits);
    setDocumentError(undefined);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.clientCode) {
        return;
      }
      try {
        await getGroupRequests(token, user?.clientCode);
      } catch (error) {
        console.log(error);
      }
    };
    fetchData();
  }, [getGroupRequests, token, user?.clientCode]);

  const renderFooter = useCallback(() => (
    <View style={styles.containerButton}>
      <ButtonComponent
        title="Solicitar inclusão"
        onPress={async () => {
          await logEvent(
            analyticsInstance,
            'request_company_submit',
            { 
              client_code: user?.clientCode,
              documents_count: documentsList.length,
              documents: documentsList.join(', ')
            }
          );
          await handleSubmit(onSubmit)();
        }}
        disabled={!(documentsList?.length > 0)}
        isLoading={loadingSubmit}
      />
      <ButtonComponent
        title="Cancelar"
        onPress={() => { 
          logEvent(analyticsInstance, 
            'request_company_cancel', 
            { 
              client_code: user?.clientCode, 
              documents_count: documentsList.length, 
              documents: documentsList.join(', ') 
            }
          );
          handleClose();
        }}
        outlined
      />
    </View>
  ), [documentsList.length, handleSubmit, onSubmit]);

  return (
    <KeyboardAvoidingView
      keyboardVerticalOffset={Platform.OS === 'ios' ? 50 : 0}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      style={{flex: 1}}>

      <View style={{ flex: 1, backgroundColor: 'white' }}>
        <Header storeAccess={false} />
        <View style={styles.topContainer}>
          <Text style={styles.filterText}>Solicite a inclusão</Text>
          <View style={styles.formContainer}>
            {
              user?.current_client?.group_id === null ?
                <TextField
                  label={'Nome do grupo'}
                  onChangeText={text => setValue('name', text)}
                /> :
                <TextField
                  label={'Nome do grupo'}
                  value={user?.group?.name}
                  editable={false}
                />
            }
            <View style={{ width: '100%' }}>
              <TextField
                label={'Insira os CPFs/CNPJs'}
                keyboardType="numeric"
                returnKeyType="done"
                ref={inputRef}
                value={document}
                onChangeText={handleTextChange}
                onSubmitEditing={addNumberToList}
                error={documentError}
              />
              <TouchableOpacity
                style={{
                  position: 'absolute',
                  right: 12,
                  bottom: 16,
                  backgroundColor: Colors.RedPallete.red,
                  borderRadius: 4,
                  width: 28,
                  height: 28,
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
                onPress={addNumberToList}
              >
                <Feather name="plus" size={18} color="white" />
              </TouchableOpacity>
            </View>
          </View>
        </View>

        <FlatList
          data={documentsList}
          renderItem={renderNumberItem}
          keyExtractor={(item, index) => index.toString()}
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 20 }}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={true}
        />

        {renderFooter()}
      </View>
    </KeyboardAvoidingView>
  );
};

export default RequestCompany;
